import { Component, OnInit, Input, HostListener } from '@angular/core';
import { formatDate } from '@angular/common';

import { EmployeeService } from '../../../shared/services/employee.service';
import { Employee, WelcomeEmailSendStatus } from '../../../shared/interfaces/client-data';
import { GridComponent, GridDataResult, PageChangeEvent } from '@progress/kendo-angular-grid';
import { SortDescriptor, orderBy, filterBy } from '@progress/kendo-data-query';
import { Observable } from 'rxjs';
import { ReusableFunctions, BulkUserActions } from '../../../shared/classes/reusable-functions';
import { HierarchyService } from '../../services/hierarchy.service';
import { HierarchyEntity } from '../../interfaces/hierarchy-data';
import {
    NoDataActionType,
    NoDataSourceType,
    NoDataTypes,
} from 'app/modules/shared/interfaces/no-data';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { ToastService } from '../../../shared/services/toast.service';
import { CurrentUserStateService } from '../../../shared/services/current-user-state.service';
import { ImpersonationService } from '../../../shared/services/impersonation.service';
import { ExcelExportData } from '@progress/kendo-angular-excel-export';

import { UserActionsDialogComponent } from 'app/modules/shared/components/user-actions-dialog/user-actions-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { UserService } from 'app/modules/shared/services/user.service';

@Component({
    selector: 'app-employee',
    templateUrl: './employee.component.html',
    styleUrls: ['./employee.component.scss'],
})
export class EmployeeComponent implements OnInit {
    private eventsSubscription: any;
    @Input() events: Observable<void>;
    gridView: GridDataResult;
    employeeSort: SortDescriptor[];
    pageSize = 10;
    skip = 0;
    type: 'numeric' | 'input' = 'input';
    employeeSearchTerm: string;
    filteredEmployees: Employee[];
    quickview: boolean;
    selectedEmployee: Employee;
    recordView: boolean;
    createEmployeeView: boolean;
    allEmployeesForClient: Employee[];
    hierarchies: HierarchyEntity[];
    editing: boolean;
    supervisors: Employee[];
    addEditOrViewOnly: string;
    loading: boolean;
    noEmployees: boolean;
    error: any;
    noDataType: NoDataTypes;
    exportFileName: string;
    exportData: Employee[];
    disableCreateUserButton: boolean = false;
    selectHeaderState = false;

    activeEmployeeFilter = 1; // Defaults to Active
    activeUserFilter = 0; // Defaults to All

    activeEmployeeFilterOptions = [
        { label: 'All', value: 0 },
        { label: 'Active', value: 1 },
        { label: 'Terminated', value: 2 },
    ];

    activeUserFilterOptions = [
        { label: 'All', value: 0 },
        { label: 'Active', value: 1 },
        { label: 'Inactive', value: 2 },
    ];

    constructor(
        private _employeeService: EmployeeService,
        private currentUserStateService: CurrentUserStateService,
        private impersonationService: ImpersonationService,
        private _reusableFunctions: ReusableFunctions,
        private _hierarchyService: HierarchyService,
        private userService: UserService,
        private route: ActivatedRoute,
        private router: Router,
        private toast: ToastService,
        public dialog: MatDialog
    ) {
        this.getExcelExportData = this.getExcelExportData.bind(this);
    }

    ngOnInit() {
        this.noDataType = {
            action: NoDataActionType.pageStart,
            searchTerm: '',
            componentTitle: 'Employees',
            inputSource: NoDataSourceType.table,
        };
        this.exportData = [];
        this.employeeSort = [{ field: 'lastName', dir: 'asc' }];
        this.quickview = false;
        this.recordView = false;
        this.addEditOrViewOnly = 'ViewOnly';
        let clientShortName = this.currentUserStateService.currentUser.clientShortName;
        if (clientShortName === 'LBA' && !this.impersonationService.isImpersonatingClient()) {
            return;
        }
        if (this.impersonationService.isImpersonatingClient()) {
            clientShortName = this.impersonationService.impersonateClient;
        }

        // Check router queryParams for employee id
        this.route.queryParams.subscribe((params) => {
            if (params && params['id']) {
                this.editing = false;
                this.loadEmployeesForClient(params['id']);
            } else {
                // if no queryParams exist, we must check normal route params
                this.route.params.subscribe((p) => {
                    if (p && p['id']) {
                        this.editing = false;
                        this.loadEmployeesForClient(p['id']);
                    } else {
                        // If no query params and no route params, load all employees
                        this.loadEmployeesForClient();
                    }
                });
            }
        });

        this.eventsSubscription = this.events.subscribe(() =>
            this.employeesSubModuleClickedHandler()
        );
        this._hierarchyService.getHierarchyEntities().subscribe((entities) => {
            this.hierarchies = entities;
        });

        // Go back to employees table on back click
        this.router.events.subscribe((event: NavigationStart) => {
            if (event.navigationTrigger === 'popstate') {
                this.recordView = false;
            }
        });
    }

    handleCancelClicked() {
        this.recordView = false;
        this.quickview = false;
        this.createEmployeeView = false;
        this.addEditOrViewOnly = 'viewOnly';
    }

    loadEmployeesForClient(employeeIdToRetrieve: string = '') {
        this.loading = true;
        this.error = undefined;
        this.noEmployees = false;
        this._employeeService.getAllEmployees().subscribe(
            (employees) => {
                if (employees && employees.length) {
                    this.noEmployees = false;
                    this.allEmployeesForClient = employees;

                    if (employeeIdToRetrieve) {
                        this.selectedEmployee = this.allEmployeesForClient.find(
                            (x) => x.primaryIdentifier === employeeIdToRetrieve
                        );
                        if (this.selectedEmployee) {
                            this.supervisors = this.allEmployeesForClient.filter(
                                (emp) => emp.isSupervisor
                            );
                            this.openRecordView();
                        } else {
                            this.toast.warning('Employee record not found.');
                            this.loadEmployeesIntoGridView();
                        }
                    } else {
                        this.supervisors = this.allEmployeesForClient.filter(
                            (emp) => emp.isSupervisor
                        );
                        this.loadEmployeesIntoGridView();
                    }
                } else {
                    this.noEmployees = true;
                }
                this.loading = false;

                this.filterEmployees();
            },
            (error) => {
                this.error = error;
                this.loading = false;
                throw new Error(error);
            }
        );
    }

    employeesSubModuleClickedHandler(): void {
        //treat like a cancel click
        this.handleCancelClicked();
    }

    employeeSelectionChangeHandler(event: any): void {
        if (event) {
            this.selectedEmployee = event.dataItem;

            let tmpHierarchy = this.hierarchies.find(
                (h) => h.hierarchyIdentifier === this.selectedEmployee.locationIdentifier
            );

            if (tmpHierarchy && tmpHierarchy.name) {
                this.selectedEmployee.location = tmpHierarchy.name;
            }

            this.showEmployeeQuickview();
        }
    }

    showEmployeeQuickview() {
        this.quickview = true;
    }

    closeEmployeeQuickview() {
        this.quickview = false;
        this.selectedEmployee = null;
    }

    employeePageChange({ skip, take }: PageChangeEvent): void {
        this.skip = skip;
        this.pageSize = take;
        this.filterEmployees();
    }

    loadEmployeesIntoGridView() {
        let orderedData = orderBy(this.allEmployeesForClient, this.employeeSort);
        this.setGridView(orderedData);
    }

    switchToAddEmployeeView() {
        this.createEmployeeView = true;
        this.recordView = true;
        this.editing = true;
        this.addEditOrViewOnly = 'add';
        this.selectedEmployee = this.createEmptyEmployee();
    }

    createEmptyEmployee(): Employee {
        return {
            firstName: '',
            lastName: '',
            clientShortName: null,
            primaryIdentifier: '',
            firstNameProper: '',
            employeeStatus: '',
            createDate: null,
            mobilePhone: '',
            terminationDate: null,
            isSupervisor: null,
            supervisorIdentifier: '',
            city: '',
            state: '',
            fullTime: null,
            hireDate: null,
            inActiveDate: null,
            isUser: false,
            jobTitle: '',
            lfName: '',
            location: '',
            locationIdentifier: '',
            loginEmail: '',
            nmls: '',
            permissionSetName: '',
            positionName: '',
            userGUID: '',
            userStatus: '',
            lastLoginDate: null,
            welcomeEmailExpirationDate: null,
            sendEmail: false,
            welcomeEmailStatus: WelcomeEmailSendStatus.Unknown,
            isActive: true,
            onLeave: false,
            isSelected: false,
        };
    }

    filterEmployees(): void {
        if (this.loading) return;
        if (!this.allEmployeesForClient || this.allEmployeesForClient.length === 0) return;

        this.filteredEmployees = [...this.allEmployeesForClient];
        let searchTermTokens = [];

        if (this.activeEmployeeFilter !== 0) {
            this.filteredEmployees = this.filteredEmployees.filter((employee) =>
                this.activeEmployeeFilter === 1
                    ? employee.employeeStatus == 'Active'
                    : employee.employeeStatus !== 'Active'
            );
        }

        if (this.activeUserFilter !== 0) {
            this.filteredEmployees = this.filteredEmployees.filter((employee) =>
                this.activeUserFilter === 1 ? employee.isActive : !employee.isActive
            );
        }

        if (this.employeeSearchTerm && this.employeeSearchTerm.length > 0) {
            searchTermTokens = this.employeeSearchTerm.split(' ');

            this.filteredEmployees = filterBy(this.filteredEmployees, {
                logic: 'or',
                filters: [
                    {
                        field: 'firstName',
                        operator: 'startswith',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'lastName',
                        operator: 'startswith',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'lfName',
                        operator: 'startswith',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'primaryIdentifier',
                        operator: 'startswith',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'jobTitle',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'positionName',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                    {
                        field: 'location',
                        operator: 'contains',
                        value: searchTermTokens[0],
                        ignoreCase: true,
                    },
                ],
            });
        }

        if (searchTermTokens.length > 1) {
            this.filteredEmployees = filterBy(this.filteredEmployees, {
                logic: 'or',
                filters: [
                    {
                        field: 'firstName',
                        operator: 'startswith',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                    {
                        field: 'lastName',
                        operator: 'startswith',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                    {
                        field: 'lfName',
                        operator: 'startswith',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                    {
                        field: 'jobTitle',
                        operator: 'contains',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                    {
                        field: 'positionName',
                        operator: 'contains',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                    {
                        field: 'location',
                        operator: 'contains',
                        value: searchTermTokens[1],
                        ignoreCase: true,
                    },
                ],
            });
        }
        let orderedData = orderBy(this.filteredEmployees, this.employeeSort);
        this.setGridView(orderedData);
    }

    openRecordView() {
        this.recordView = true;
        this.quickview = false;
        this.editing = false;
        this.addEditOrViewOnly = 'viewOnly';
    }

    @HostListener('window:keydown', ['$event'])
    keyEvent(event: KeyboardEvent) {
        if (event.keyCode === 27) {
            //keyCode 27 is the escape key.
            if (this.quickview) {
                this.quickview = false;
            } else if (this.recordView) {
                this.recordView = false;
            }
            this.createEmployeeView = false;
            this.addEditOrViewOnly = 'viewOnly';
        }
    }

    sortChange(sort: SortDescriptor[]): void {
        this.employeeSort = sort;
        this.filterEmployees();
    }

    updateGrid() {
        let orderedData = orderBy(
            this.filteredEmployees ? this.filteredEmployees : this.allEmployeesForClient,
            this.employeeSort
        );
        this.setGridView(orderedData);
    }

    setGridView(orderedData: Employee[]) {
        this.exportData = orderedData.map((obj) => ({
            ...obj,
            formattedHireDate: !obj.hireDate ? '' : formatDate(obj.hireDate, 'MM/dd/yyyy', 'en-US'),
            formattedTermDate: !obj.terminationDate
                ? ''
                : formatDate(obj.terminationDate, 'MM/dd/yyyy', 'en-US'),
        }));
        this.gridView = {
            data: orderedData.slice(this.skip, this.skip + this.pageSize),
            total: orderedData.length,
        };
    }

    handleEmployeeModEvent() {
        this.createEmployeeView = false;
        this.addEditOrViewOnly = 'viewOnly';
        this.loadEmployeesForClient();
        this.recordView = true;
        this.editing = false;
    }

    ngOnDestroy() {
        if (this.eventsSubscription) {
            this.eventsSubscription.unsubscribe();
        }
    }

    exportToExcel(kendoGrid: GridComponent) {
        let dateTime = this._reusableFunctions.getCurrentDateTimeStamp();
        this.exportFileName = 'nv-employees_' + dateTime + '_.xlsx';
        kendoGrid.saveAsExcel();
    }

    getExcelExportData(): ExcelExportData {
        const result: ExcelExportData = {
            data: this.exportData,
            group: [],
        };
        return result;
    }

    async switchToConvertUsersView() {
        let convertList: Employee[] = [];
        this.disableCreateUserButton = true;
        try {
            for (const employee of this.allEmployeesForClient) {
                if (
                    //this.currentSelection.includes(employee.primaryIdentifier) &&
                    employee.isSelected &&
                    !employee.userGUID
                ) {
                    employee.actionToggle = employee.convertToUser;
                    employee.convertToUser = true;
                    // Check user table to prevent duplicate Users (email)
                    const emailAddressCheck = {
                        emailAddress: employee.loginEmail,
                    };
                    let data = await this.userService
                        .isLoginEmailAvailable(emailAddressCheck)
                        .toPromise();
                    if (data) {
                        convertList.push(employee);
                    }
                }
            }
        } catch (err) {
        } finally {
            this.disableCreateUserButton = false;
        }

        /*this.allEmployeesForClient.filter((employee) =>
            this.currentSelection.includes(employee.primaryIdentifier)
        );
        */

        const convertDialogRef = this.dialog.open(UserActionsDialogComponent, {
            data: { employees: convertList, action: BulkUserActions.ConvertFromEmployee },
        });
    }

    onSelectHeaderChange() {
        this.allEmployeesForClient?.forEach((item) => {
            item.isSelected = this.selectHeaderState;
        });
    }
}
